import type { CanonicalEntry } from "../segmenting/canonical-entry.js";
import { glossNodes } from "./segmenter-cells.js";

/**
 * The meanings on the page, filled in and opened.
 *
 * Apart from `segmenter-view.ts`, which draws the table. A meaning arrives
 * after its row does and is written into the cell where it stands, so this is
 * the only part of the list that changes without a draw.
 */

/** What this needs told. */
export interface SegmenterMeaningsProperties {
  /** The table body the rows are in. */
  readonly list: HTMLElement;

  /**
   * What the site says a word means, where this page has been told.
   *
   * Required, unlike the rest of this. A cell only ever holds a control once
   * an entry has reached it, so a press that could not be answered would mean
   * the two halves had come apart.
   */
  readonly entryOf: (headword: string) => CanonicalEntry | undefined;

  /** Called with a headword when a reader opens its meaning. */
  readonly onOpened?: ((headword: string) => void) | undefined;
}

/** One page's meanings, and which of them a reader has opened. */
export class SegmenterMeanings {
  readonly #properties: SegmenterMeaningsProperties;

  /**
   * The rows whose meaning a reader has opened.
   *
   * Kept here rather than on the cell, because every draw replaces the cell. A
   * reader who opened 打 and then reordered the list would otherwise find it
   * closed again.
   */
  readonly #open = new Set<string>();

  constructor(properties: SegmenterMeaningsProperties) {
    this.#properties = properties;
    this.#takePresses();
  }

  /** Which rows are open, for a draw that is about to replace them all. */
  opened(): ReadonlySet<string> {
    return this.#open;
  }

  /**
   * Fill in one word's meaning, wherever the current list shows it.
   *
   * One cell rather than the whole table. A few hundred answers arrive one at
   * a time over a few seconds, and redrawing the list for each of them would
   * move the rows under a reader's pointer that many times.
   *
   * Nothing happens where the word is no longer on the page, which is a reader
   * who typed on while the answer was on its way.
   */
  show(headword: string, entry: CanonicalEntry | undefined): void {
    const cells = this.#properties.list.querySelectorAll<HTMLElement>(
      `[data-cb-gloss="${CSS.escape(headword)}"]`,
    );

    for (const cell of cells) {
      const word =
        cell.closest("tr")?.querySelector(".cb-segmenter-hanzi")?.textContent ??
        "";

      cell.replaceChildren(
        ...glossNodes(entry, this.#open.has(headword), word),
      );
    }
  }

  /**
   * Take the presses on the meanings, once, for every row there will ever be.
   *
   * One listener on the table body rather than one per cell. The body itself
   * survives every draw, so a listener on it covers rows that do not exist
   * yet, and the few hundred rows of a pasted article carry no listeners of
   * their own.
   */
  #takePresses(): void {
    this.#properties.list.addEventListener("click", (event: Event) => {
      const pressed = event.target;
      const headword =
        pressed instanceof Element
          ? pressed
              .closest(".cb-segmenter-meaning")
              ?.closest<HTMLElement>("[data-cb-gloss]")?.dataset["cbGloss"]
          : undefined;

      if (headword !== undefined) {
        this.#toggle(headword);
      }
    });
  }

  /** Open or close one word's meaning, and report the first opening. */
  #toggle(headword: string): void {
    if (this.#open.has(headword)) {
      this.#open.delete(headword);
    } else {
      this.#open.add(headword);
      this.#properties.onOpened?.(headword);
    }

    this.show(headword, this.#properties.entryOf(headword));
  }
}
